import { formatSafeDisplayText } from "./taskWorkspaceProjection";

const HONOR_PREFIX = /^Honor the product decision:\s*/i;

const UNRELATED_PATTERNS = [
  /\bPII\b/i,
  /personally identifiable/i,
  /file.?retention/i,
  /retention period/i,
  /file.?size/i,
  /sensitive.?data/i,
  /\bGDPR\b/i,
  /credit card/i,
  /social security/i,
];

const TOPIC_RULES = [
  { id: "behavior", label: "Main behavior", pattern: /\b(outcome|consistently|regardless of case|product outcome|request|response|schema|duplicate|content-type|canonical)\b/i },
  { id: "compatibility", label: "Compatibility", pattern: /\b(compatib|lowercase|backward|existing)\b/i },
  { id: "failure", label: "Failure handling", pattern: /\b(invalid|fail|error|reject|throw)\b/i },
  { id: "developer", label: "Types and documentation", pattern: /\b(types?|typescript|documentation|documents?|error contract|dx)\b/i },
  { id: "verification", label: "Testing", pattern: /\b(tests?|ci|coverage|verify|assertion)\b/i },
  { id: "deferred", label: "Deferred or unrelated items", pattern: /\b(defer|not applicable|excluded|non-goal|out of scope)\b/i },
];

export function requirementDisplayText(raw) {
  const text = formatSafeDisplayText(raw, "");
  return text.replace(HONOR_PREFIX, "").trim() || text;
}

export function inferRequirementRelevance(text) {
  const value = requirementDisplayText(text);
  if (UNRELATED_PATTERNS.some((pattern) => pattern.test(value))) {
    return { id: "unrelated", label: "Possibly unrelated", relevance: "unrelated" };
  }
  return { id: "relevant", label: "Relevant", relevance: "relevant" };
}

export function inferRequirementTopic(text) {
  const value = requirementDisplayText(text);
  if (inferRequirementRelevance(value).relevance === "unrelated") {
    return { id: "unrelated", label: "Possibly unrelated" };
  }
  const match = TOPIC_RULES.find((rule) => rule.pattern.test(value));
  return match || { id: "other", label: "Other requirements" };
}

export function groupRequirementResults(results = []) {
  const groups = new Map();
  (results || []).forEach((result, index) => {
    const text = requirementDisplayText(result?.criterionText || result?.text || result);
    const topic = inferRequirementTopic(text);
    if (!groups.has(topic.id)) {
      groups.set(topic.id, { id: topic.id, label: topic.label, items: [] });
    }
    groups.get(topic.id).items.push({
      ...result,
      displayText: text,
      originalIndex: index,
    });
  });
  const order = [...TOPIC_RULES.map((rule) => rule.id), "other", "unrelated"];
  return order
    .map((id) => groups.get(id))
    .filter(Boolean)
    .map((group) => ({
      ...group,
      counts: {
        supported: group.items.filter((item) => item.status === "met").length,
        partial: group.items.filter((item) => item.status === "partially_met").length,
        unsupported: group.items.filter((item) => item.status === "not_met").length,
        unverifiable: group.items.filter((item) => item.status === "cannot_verify").length,
      },
    }));
}

export function requirementScorecard(results = []) {
  return {
    supported: results.filter((item) => item.status === "met").length,
    partial: results.filter((item) => item.status === "partially_met").length,
    unsupported: results.filter((item) => item.status === "not_met").length,
    unverifiable: results.filter((item) => item.status === "cannot_verify").length,
    notApplicable: results.filter((item) => item.status === "not_applicable").length,
    total: results.length,
  };
}
